import { Controller } from "@hotwired/stimulus"

/**
 * Share Privacy Controller
 * 
 * Manages share privacy toggle functionality.
 * Handles Open/Closed state and removing all permissions when Open is selected.
 * 
 * Usage: Add data-controller="share-privacy" to the share form content
 */
export default class extends Controller {
  static targets = ["permissionsSection"]
  
  connect() {
    // Use event delegation - listen on the element itself for change events from radios
    this.boundToggle = this.toggle.bind(this)
    this.element.addEventListener('change', this.boundToggle)
    
    // Set initial icon colors
    this.updateIconColors()
  }
  
  disconnect() {
    // Clean up event listeners
    if (this.boundToggle) {
      this.element.removeEventListener('change', this.boundToggle)
    }
  }
  
  toggle(event) {
    // Only handle events from privacy radios
    if (!event.target.classList.contains('privacy-radio')) {
      return
    }
    
    const radio = event.target
    const isOpen = radio.id === 'project_locked_false' && radio.checked
    
    // Update all icon colors in the privacy section
    this.updateIconColors()
    
    if (isOpen) {
      // Check if there are any permissions (excluding current user)
      const hasPermissions = this.hasPermissions()
      
      if (hasPermissions) {
        if (confirm('Are you sure you want to make this project open? All shared permissions will be removed.')) {
          this.submitUnshareAll()
        } else {
          // User cancelled, restore to closed
          const closedRadio = this.element.querySelector('#project_locked_true')
          if (closedRadio) {
            closedRadio.checked = true
            this.updateIconColors()
          }
        }
      }
    }
  }
  
  updateIconColors() {
    // Find all radios in the privacy-section and update their icon colors
    const section = this.element.querySelector('.privacy-section')
    if (section) {
      section.querySelectorAll('.privacy-radio').forEach(radio => {
        const icon = radio.nextElementSibling
        if (icon && icon.classList.contains('privacy-icon')) {
          if (radio.checked) {
            icon.style.color = '#000000' // Black when checked
          } else {
            icon.style.color = '#d1d5db' // Light gray when unchecked
          }
        }
      })
    }
  }
  
  hasPermissions() {
    // Check if there are any permissions in the existing-users section (excluding current user)
    const permissionsSection = this.element.querySelector('.existing-users')
    if (!permissionsSection) return false
    
    // Count user rows - we need at least 1 (the current user), so if there's more than 1, there are other users
    const userRows = permissionsSection.querySelectorAll('.user-row')
    return userRows.length > 1 // More than just the current user
  }
  
  submitUnshareAll() {
    // Submit the form with locked=false to make it open
    // The backend will handle removing permissions when unshare_all is set
    const form = this.element.querySelector('form')
    if (!form) return
    
    // Set locked to false
    const lockedFalseRadio = this.element.querySelector('#project_locked_false')
    if (lockedFalseRadio) {
      lockedFalseRadio.checked = true
    }
    
    // Add a hidden input to signal unshare all
    let unshareAllInput = form.querySelector('input[name="unshare_all"]')
    if (!unshareAllInput) {
      unshareAllInput = document.createElement('input')
      unshareAllInput.type = 'hidden'
      unshareAllInput.name = 'unshare_all'
      unshareAllInput.value = 'true'
      form.appendChild(unshareAllInput)
    }
    
    // Submit the form
    form.submit()
  }
}
